// hooks/usePageTracking.ts
import { useEffect } from "react";
import siteMap, { SiteMapItem } from "@/config/siteMap";
import { analytics } from "@/utils";

const GA_TRACKING_ID = import.meta.env.VITE_GOOGLE_TAG;
const APP_NAME = "FormSubmits";

// Convert siteMap URL pattern to regex (handles :param segments)
const urlToRegex = (url: string): RegExp => {
  // First replace :param with a placeholder
  // Then escape special regex chars
  // Then restore the placeholder as regex pattern
  const PLACEHOLDER = "___PARAM___";

  let pattern = url.replace(/:([a-zA-Z_][a-zA-Z0-9_]*)/g, PLACEHOLDER);
  pattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  pattern = pattern.replaceAll(PLACEHOLDER, "[^/]+");

  return new RegExp(`^${pattern}/?$`); // Allow optional trailing slash
};

// Recursively find matching siteMap item
const findSiteMapItem = (
  pathname: string,
  items: SiteMapItem[]
): SiteMapItem | null => {
  for (const item of items) {
    // Check nested items first (more specific routes)
    if (item.items?.length) {
      const found = findSiteMapItem(pathname, item.items);
      if (found) return found;
    }
    // Then check current item
    if (item.url && urlToRegex(item.url).test(pathname)) {
      return item;
    }
  }
  return null;
};

const getPageTitle = (pathname: string): string => {
  const item = findSiteMapItem(pathname, siteMap);
  return item?.name ? `${item.name} | ${APP_NAME}` : APP_NAME;
};

export const usePageTracking = () => {
  useEffect(() => {
    const trackPageView = () => {
      const pathname = window.location.pathname;
      const search = window.location.search;

      // Update document title
      const title = getPageTitle(pathname);
      document.title = title;

      // Track in GA
      if (typeof window.gtag === "function" && GA_TRACKING_ID) {
        analytics.pageView(pathname + search, title);
      }
    };

    trackPageView();

    window.addEventListener("popstate", trackPageView);

    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(this, args);
      trackPageView();
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      trackPageView();
    };

    return () => {
      window.removeEventListener("popstate", trackPageView);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);
};
